/* ============================================================
   PHONE AUTOMATION — Control Panel (SEBENAR, belakang suis v4)
   ============================================================
   Toggle DALAM Control Panel sedia ada (keputusan user 23 Okt: "patutnya
   kat control panel ada toggle on phone automation, then pilih post ke apps
   mana"). Menggantikan entry lama yang membuka page berasingan.

   · OFF = Control Panel biasa (robot extension TikTok/FB) — App.jsx render
     seksyen sedia ada SEPERTI BIASA, komponen ini cuma papar baris toggle.
   · ON  = mod telefon: App.jsx SOROK seksyen extension, komponen ini papar
     "Post to" (pilih app) + "How to post" (Post now / Schedule / Save as
     draft) + butang buka halaman posting.

   Schedule BUKAN dibuang (arahan user 27 Okt — betulkan assume aku 23 Okt):
   app masa depan (FB Reels) BOLEH schedule ikut slot kalendar. "Schedule"
   HANYA dikunci bila app terpilih `schedulable:false` (cth Shopee Video —
   telefon perlu bersambung). Kunci guna design SAMA "How to post" asal.

   ⛔ Enjin bulk hands-off BELUM siap. Connect telefon + pilih video + run
   sebenar duduk di halaman /phone-automation (keputusan user: page kekal
   untuk "setup telefon kali pertama"). Butang di sini membukanya — BUKAN
   kawalan palsu; ia bawa ke tempat kerja sebenar berlaku. Pilihan app +
   cara post disimpan per-browser dan dibaca semula oleh halaman itu.

   Enjin universal (banyak apps): Shopee Video = app pertama; Facebook Reels
   / TikTok menyusul. ⛔ Jangan sempitkan ke Shopee sahaja.

   UI = English (masuk i18n + pil EN/BM sebelum release). Rupa v3.9 monokrom.
   ============================================================ */

import { useEffect, useState } from 'react';
import { Smartphone } from 'lucide-react';
// Guna SEMULA design "How to post" asal (senarai radio .s-cara) supaya rupa
// identik dgn Control Panel sedia ada (arahan user 27 Okt). CSS sahaja —
// markup di sini kerana pilihan telefon tidak sama persis (Schedule dikunci
// ikut app, bukan ikut tab).
import './CaraPostV39.css';
// Daftar apps = SATU sumber dikongsi (utils/phoneApps) — elak senarai kembar.
import { PHONE_APPS, PHONE_CONTENT_LABEL, bacaPhoneContent, bacaPhoneAiLabel, bacaPhoneFbPage, bacaPhoneFbLink, bacaPhoneTtBag } from '../utils/phoneApps';
import { telefonSedia } from '../utils/androidCalibration';
import { lepaskanTelefon } from '../utils/androidAdb';
import { loadAdminDefault } from '../utils/androidDefault';

const SECT = { fontSize: 12, fontWeight: 600, color: 'var(--s-ink-2)', margin: '0 0 8px' };
const LINK = { background: 'none', border: 'none', padding: 0, font: 'inherit', fontSize: 12, color: 'var(--s-brand)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' };
const GRID3 = { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 };

/* Kad "pilih satu" — rupa sama PostToV39 / CaraPostV39 (monokrom, dipilih =
   isian gelap). locked = "Soon" (tak boleh tekan). */
function PickCard({ on, locked, onClick, icon, label, soon }) {
  return (
    <button
      type="button" role="radio" aria-checked={on} disabled={locked}
      onClick={() => { if (!locked && !on && onClick) onClick(); }}
      style={{
        position: 'relative', minWidth: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
        padding: '10px 5px', font: 'inherit', textAlign: 'center', lineHeight: 1.2,
        cursor: locked ? 'not-allowed' : on ? 'default' : 'pointer', borderRadius: 'var(--s-r)',
        background: on ? 'var(--s-primary)' : 'var(--s-fill)',
        border: on ? '1px solid var(--s-primary)' : locked ? '1px dashed var(--s-line-strong)' : '1px solid var(--s-line-strong)',
        color: on ? 'var(--s-primary-ink)' : 'var(--s-ink-3)', opacity: locked ? 0.55 : 1,
      }}
    >
      {icon}
      <span style={{ fontSize: 12, fontWeight: on ? 700 : 500 }}>{label}</span>
      {soon && <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.03em', color: 'var(--s-ink-3)' }}>SOON</span>}
    </button>
  );
}

export default function PhoneControlPanelV4({ on, onToggle, app, onApp, route, onRoute, onSetup }) {
  const current = PHONE_APPS.find((a) => a.id === app) || PHONE_APPS[0];
  const scheduleLocked = !current.schedulable;
  // ★ [2026-11-04] draftOnly (TikTok — keputusan user "buat semua draft dulu"): Post now +
  // Schedule dikunci, route dipaksa 'draft'.
  const draftOnly = !!current.draftOnly;
  // App tak boleh schedule (cth Shopee) + route tersimpan 'schedule' → patah
  // balik ke kosong supaya pilihan terkunci tidak kekal "dipilih".
  useEffect(() => {
    if (draftOnly) { if (route !== 'draft') onRoute('draft'); return; }
    if (scheduleLocked && route === 'schedule') onRoute('');
  }, [draftOnly, scheduleLocked, route, onRoute]);

  // ★ [2026-11-04] Jenis post + AI label (app yang menyokong). Simpanan per-browser,
  // dibaca semula oleh utils/phoneRunDump.js ketika Run (bacaPhoneContent / bacaPhoneAiLabel).
  const [content, setContent] = useState(() => bacaPhoneContent(current));
  const [aiLabel, setAiLabel] = useState(() => bacaPhoneAiLabel());
  useEffect(() => { setContent(bacaPhoneContent(current)); }, [current]);
  const pilihContent = (c) => {
    setContent(c);
    try { localStorage.setItem('sesaat_phone_content_v1', c); } catch { /* storan ditutup */ }
  };
  const tukarAiLabel = () => {
    const v = !aiLabel;
    setAiLabel(v);
    try { localStorage.setItem('sesaat_phone_ailabel_v1', v ? '1' : '0'); } catch { /* storan ditutup */ }
  };
  // FB: page untuk post (kosong = profile) + link Shopee pada reel. Dibaca phoneRunDump.
  const [fbPage, setFbPage] = useState(() => bacaPhoneFbPage());
  const [fbLink, setFbLink] = useState(() => bacaPhoneFbLink());
  const simpanFbPage = (v) => {
    setFbPage(v);
    try { localStorage.setItem('sesaat_phone_fbpage_v1', v.trim()); } catch { /* storan ditutup */ }
  };
  const tukarFbLink = () => {
    const v = !fbLink;
    setFbLink(v);
    try { localStorage.setItem('sesaat_phone_fblink_v1', v ? '1' : '0'); } catch { /* storan ditutup */ }
  };
  // TikTok: beg kuning pada video. Dibaca phoneRunDump (bacaPhoneTtBag).
  const [ttBag, setTtBag] = useState(() => bacaPhoneTtBag());
  const tukarTtBag = () => {
    const v = !ttBag;
    setTtBag(v);
    try { localStorage.setItem('sesaat_phone_ttbag_v1', v ? '1' : '0'); } catch { /* storan ditutup */ }
  };

  // Status setup: dibaca semula bila panel dibuka atau user kembali dari page setup.
  const [sedia, setSedia] = useState(null);
  const [notaLepas, setNotaLepas] = useState('');
  const lepaskan = async () => {
    try {
      const n = await lepaskanTelefon();
      setNotaLepas(n ? 'Phone released. You can now connect it in another browser.' : 'Phone is free here. If another browser is using it, release it there.');
    } catch {
      setNotaLepas('Could not release the phone. Unplug the USB cable and plug it back in.');
    }
  };
  useEffect(() => {
    if (!on) return undefined;
    let batal = false;
    const baca = async () => {
      // Enjin 'dump' (TikTok) tiada kalibrasi → tiada Test wajib.
      if (current.engine === 'dump') { if (!batal) setSedia({ model: 'run a test if the robot gets stuck' }); return; }
      const def = await loadAdminDefault(current.id);
      if (!batal) setSedia(telefonSedia(current.id, def.version));
    };
    baca();
    window.addEventListener('focus', baca);
    return () => { batal = true; window.removeEventListener('focus', baca); };
  }, [on, current.id, current.engine]);

  const ROUTES = [
    { id: 'now', label: 'Post now', note: 'Publishes straight to the app on your phone.' },
    {
      id: 'schedule', label: 'Schedule',
      note: draftOnly
        ? `${current.label} on the phone saves drafts only for now.`
        : scheduleLocked
        ? `${current.label} can't be scheduled — your phone must be connected when posting.`
        : 'The robot schedules each video at its ticked calendar slot.',
    },
    { id: 'draft', label: 'Save as draft', note: 'Saved as a draft in the app on your phone — publish it yourself later.' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: on ? 16 : 0, borderBottom: '1px solid var(--s-line)', marginBottom: 16 }}>
      {/* ── TOGGLE ── sentiasa nampak (ON dan OFF) supaya boleh patah balik. */}
      <div style={{
        border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r)', padding: '10px 12px',
        background: on ? 'var(--s-brand-soft)' : 'transparent',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <Smartphone size={17} strokeWidth={2} style={{ color: on ? 'var(--s-brand)' : 'var(--s-ink-2)', flex: '0 0 auto' }} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Phone automation</span>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-2)' }}>
              {on ? 'Post straight from your connected phone' : 'Off — posting via the SESAAT extension robot'}
            </span>
          </span>
        </span>
        <button type="button" role="switch" aria-checked={on} onClick={() => onToggle(!on)}
          aria-label="Phone automation"
          style={{
            flex: '0 0 auto', width: 42, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 2,
            background: on ? 'var(--s-brand)' : 'var(--s-line-strong)', transition: 'background .15s',
            display: 'flex', justifyContent: on ? 'flex-end' : 'flex-start', alignItems: 'center',
          }}>
          <span style={{ width: 20, height: 20, borderRadius: 999, background: '#fff', display: 'block' }} />
        </button>
        </div>

        {/* Status setup: lakaran A (user 1 Nov), dalam kotak yang sama supaya jelas milik Phone automation. */}
        {on && (sedia ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--s-line-strong)' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--s-ok)', minWidth: 0 }}>Phone set up{sedia.model ? ` · ${sedia.model}` : ''}</span>
            <span style={{ flex: '0 0 auto', display: 'flex', gap: 10 }}>
              {/* Label "Set up & test" = nama tetap untuk ayat sokongan (keputusan user 4 Nov). */}
              <button type="button" onClick={onSetup} style={LINK}>Set up &amp; test</button>
              <button type="button" onClick={lepaskan} style={LINK}>Release phone</button>
            </span>
          </div>
        ) : (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--s-line-strong)' }}>
            <span style={{ display: 'block', fontSize: 12, fontWeight: 600 }}>Your phone is not set up yet</span>
            <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-2)', margin: '2px 0 10px' }}>Connect it and run one test before posting.</span>
            <button type="button" onClick={onSetup} style={{ width: '100%', height: 36, background: 'var(--s-primary)', color: 'var(--s-primary-ink)', border: '1px solid var(--s-primary)', borderRadius: 10, fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>
              Set up &amp; test
            </button>
            <button type="button" onClick={lepaskan} style={{ ...LINK, display: 'block', margin: '8px auto 0' }}>Release phone</button>
          </div>
        ))}
        {on && notaLepas && <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-2)', marginTop: 8 }}>{notaLepas}</span>}
      </div>

      {on && (
        <>
          {/* ── Post To ── pilih app telefon. */}
          <div>
            <p style={SECT}>Post to</p>
            <div style={GRID3}>
              {PHONE_APPS.map((a) => (
                <PickCard
                  key={a.id}
                  on={a.ready && app === a.id}
                  locked={!a.ready}
                  soon={!a.ready}
                  onClick={() => onApp(a.id)}
                  icon={<Smartphone size={20} strokeWidth={2} />}
                  label={a.label}
                />
              ))}
            </div>
            <p style={{ fontSize: 11, color: 'var(--s-ink-2)', margin: '8px 0 0' }}>
              Runs on the app installed on your phone.
            </p>
          </div>

          {/* ── What to post ── jenis post (app yang menyokong lebih satu jenis). */}
          {Array.isArray(current.contents) && current.contents.length > 1 && (
            <div>
              <p style={SECT}>What to post</p>
              <div style={GRID3}>
                {current.contents.map((c) => (
                  <PickCard
                    key={c}
                    on={content === c}
                    onClick={() => pilihContent(c)}
                    icon={<span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden="true">{c === 'video' ? 'movie' : c === 'image' ? 'photo_library' : 'notes'}</span>}
                    label={PHONE_CONTENT_LABEL[c] || c}
                  />
                ))}
              </div>
              <p style={{ fontSize: 11, color: 'var(--s-ink-2)', margin: '8px 0 0', lineHeight: 1.5 }}>
                {content === 'video' && (current.fbOptions ? 'Posted as a Reel. ' : '')}
                {content === 'video' && 'Uses the first set on each card that has a video.'}
                {content === 'image' && 'Uses the card images in their card order (up to 35).'}
                {content === 'text' && 'Uses the card caption as the text post.'}
              </p>
            </div>
          )}

          {/* ── Facebook: post sebagai page + link Shopee (reel). ── */}
          {current.fbOptions && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <p style={SECT}>Post as</p>
                <input
                  type="text" value={fbPage} onChange={(e) => simpanFbPage(e.target.value)}
                  placeholder="Your profile (leave empty) or exact Page name"
                  style={{ width: '100%', boxSizing: 'border-box', height: 36, padding: '0 10px', fontSize: 12, border: '1px solid var(--s-line-strong)', borderRadius: 10, background: 'var(--s-fill)', color: 'var(--s-ink)' }}
                />
                <p style={{ fontSize: 11, color: 'var(--s-ink-2)', margin: '6px 0 0', lineHeight: 1.5 }}>
                  Type the Page name exactly as it shows in the Facebook app. The robot switches back to your profile after.
                </p>
              </div>
              {content === 'video' && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r)', padding: '10px 12px' }}>
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Shopee product link</span>
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-2)' }}>
                      Adds the card&apos;s Shopee link to the Reel. Needs a Page. The Shopee banner only shows after that Page connects its affiliate account in Facebook.
                    </span>
                  </span>
                  <button type="button" role="switch" aria-checked={fbLink} onClick={tukarFbLink} aria-label="Shopee product link"
                    style={{
                      flex: '0 0 auto', width: 42, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 2,
                      background: fbLink ? 'var(--s-brand)' : 'var(--s-line-strong)', transition: 'background .15s',
                      display: 'flex', justifyContent: fbLink ? 'flex-end' : 'flex-start', alignItems: 'center',
                    }}>
                    <span style={{ width: 20, height: 20, borderRadius: 999, background: '#fff', display: 'block' }} />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── TikTok: beg kuning (video). ── */}
          {current.bag && content === 'video' && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r)', padding: '10px 12px' }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>Yellow bag</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-2)' }}>
                  Adds the card&apos;s TikTok product, found by its name. Needs a TikTok Shop affiliate account. Uses your CTA text.
                </span>
              </span>
              <button type="button" role="switch" aria-checked={ttBag} onClick={tukarTtBag} aria-label="Yellow bag"
                style={{
                  flex: '0 0 auto', width: 42, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 2,
                  background: ttBag ? 'var(--s-brand)' : 'var(--s-line-strong)', transition: 'background .15s',
                  display: 'flex', justifyContent: ttBag ? 'flex-end' : 'flex-start', alignItems: 'center',
                }}>
                <span style={{ width: 20, height: 20, borderRadius: 999, background: '#fff', display: 'block' }} />
              </button>
            </div>
          )}

          {/* ── AI label ── suis ON/OFF (app yang ada label AI). */}
          {current.aiLabel && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, border: '1px solid var(--s-line-strong)', borderRadius: 'var(--s-r)', padding: '10px 12px' }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>AI label</span>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--s-ink-2)' }}>Mark the post as AI-generated content</span>
              </span>
              <button type="button" role="switch" aria-checked={aiLabel} onClick={tukarAiLabel} aria-label="AI label"
                style={{
                  flex: '0 0 auto', width: 42, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', padding: 2,
                  background: aiLabel ? 'var(--s-brand)' : 'var(--s-line-strong)', transition: 'background .15s',
                  display: 'flex', justifyContent: aiLabel ? 'flex-end' : 'flex-start', alignItems: 'center',
                }}>
                <span style={{ width: 20, height: 20, borderRadius: 999, background: '#fff', display: 'block' }} />
              </button>
            </div>
          )}

          {/* ── How to post ── design SAMA "How to post" asal (.s-cara). 3 pilihan;
              "Schedule" dikunci (ikon kunci + sebab) bila app tak boleh schedule. */}
          <div>
            <p style={SECT}>How to post</p>
            <div className="s-cara" role="radiogroup" aria-label="How to post">
              {ROUTES.map((r) => {
                const locked = (r.id === 'schedule' && scheduleLocked) || (draftOnly && r.id !== 'draft');
                const aktif = route === r.id && !locked;
                return (
                  <button
                    key={r.id}
                    type="button" role="radio" aria-checked={aktif} disabled={locked}
                    className={'s-cara__baris' + (aktif ? ' s-cara__baris--aktif' : '')}
                    onClick={() => { if (!locked && route !== r.id) onRoute(r.id); }}
                    title={locked ? (draftOnly ? `${current.label} saves drafts only for now.` : r.note) : undefined}
                  >
                    <span className="s-cara__bulat" aria-hidden="true" />
                    <span className="s-cara__txt">
                      <span className="s-cara__lbl">{r.label}</span>
                      <span className="s-cara__nota">{r.note}</span>
                    </span>
                    {locked && <span className="material-symbols-outlined s-cara__lock" aria-hidden="true">lock</span>}
                  </button>
                );
              })}
            </div>
            <p style={{ fontSize: 11, color: 'var(--s-ink-2)', margin: '8px 0 0', lineHeight: 1.5 }}>
              Tick your cards or calendar slots, then press <b>Post to phone</b> below.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
